"use client";

import { useMemo, useRef, useState } from "react";
import {
  Checkbox,
  ErrorNotice,
  Notice,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  CARD,
  HINT,
  TABLE,
  TD,
  TH,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useLocalToday } from "@/components/tools/study/controls";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { downloadText } from "@/lib/tool-kit/download";
import {
  type DayNumber,
  dayFormatter,
  isoFromDay,
  parseIsoDay,
  randomToken,
} from "@/lib/tool-kit/study/dates";
import {
  buildIcsCalendar,
  ICS_MIME,
  type IcsAllDayEvent,
  icsUidFactory,
} from "@/lib/tool-kit/study/ics";
import {
  DEFAULT_INTERVALS,
  mergeReviewDays,
  parseIntervals,
  type ReviewDay,
  type TopicReviews,
  topicReviews,
} from "@/lib/tool-kit/study/spaced-repetition";

const MAX_SPAN_DAYS = 730;
const DEFAULT_EXAM_DAYS = 28;
const DEFAULT_INTERVAL_TEXT = DEFAULT_INTERVALS.join(", ");
const EXAMPLE_STARTS = [0, 2, 5];

/** English strings. A translated content file overrides these through `ui`. */
export const SPACED_REPETITION_SCHEDULER_STRINGS = {
  topicsHeading: "Topics",
  topicLabel: "Topic",
  topicPlaceholder: "e.g. Cell division",
  firstStudyLabel: "First studied on",
  removeTopic: "Remove topic",
  addTopic: "Add topic",
  loadExample: "Try an example",
  exampleTopics: ["Photosynthesis", "Cell respiration", "Mitosis and meiosis"],
  examLabel: "Exam date",
  intervalsLabel: "Review intervals (days after first study)",
  intervalsHint:
    "Whole days separated by commas. 1, 3, 7, 14, 30 means reviews one, three, seven, fourteen and thirty days after you first study a topic.",
  resetIntervals: "Reset intervals",
  finalReview: "Add a last review the day before the exam",
  results: "Your Review Schedule",
  topicsStat: "Topics",
  reviewsStat: "Review sessions",
  daysStat: "Days with reviews",
  calendarHeading: "Review calendar",
  colDate: "Date",
  colReviews: "Review",
  reviewItem: "{topic} ({number} of {total})",
  examRow: "Exam day",
  perTopicHeading: "By topic",
  reviewDate: "{date} (day {offset})",
  compressed:
    "Intervals squeezed to fit before the exam: last review the day before it.",
  finalAdded: "Last review added the day before the exam.",
  noReviews:
    "{topic}: first studied too close to the exam for any review — study it earlier if you can.",
  downloadIcs: "Add to calendar (.ics)",
  icsFileName: "spaced-repetition.ics",
  icsCalendarName: "Spaced repetition",
  icsSummary: "Review: {topics}",
  icsDescription: "{items}",
  icsExam: "Exam",
  enterTopics:
    "Name at least one topic and choose your exam date to see the review schedule.",
  enterIntervals:
    "Enter at least one review interval in whole days, such as 1, 3, 7.",
  examBeforeTopics:
    "The exam date is before every first-study date. Pick a later exam date.",
  tooLong:
    "Plans run for up to {max} days. Pick an exam date closer to the start.",
} as const;

type Strings = typeof SPACED_REPETITION_SCHEDULER_STRINGS;

interface TopicRow {
  id: string;
  name: string;
  first: string;
}

interface Topic {
  name: string;
  first: DayNumber;
  plan: TopicReviews;
}

interface Built {
  topics: Topic[];
  exam: DayNumber | null;
  message: string | null;
  error: boolean;
}

function build(
  rows: readonly TopicRow[],
  examText: string,
  intervals: readonly number[],
  finalReview: boolean,
  strings: Strings
): Built {
  const exam = parseIsoDay(examText);
  const named = rows
    .map((row) => ({ name: row.name.trim(), first: parseIsoDay(row.first) }))
    .filter(
      (row): row is { name: string; first: DayNumber } =>
        row.name !== "" && row.first !== null
    );
  if (exam === null || named.length === 0) {
    return { topics: [], exam, message: strings.enterTopics, error: false };
  }
  if (intervals.length === 0 && !finalReview) {
    return { topics: [], exam, message: strings.enterIntervals, error: false };
  }
  if (named.every((row) => row.first >= exam)) {
    return { topics: [], exam, message: strings.examBeforeTopics, error: true };
  }
  const earliest = Math.min(...named.map((row) => row.first));
  if (exam - earliest > MAX_SPAN_DAYS) {
    return {
      topics: [],
      exam,
      message: t(strings.tooLong, { max: MAX_SPAN_DAYS }),
      error: true,
    };
  }
  const topics = named.map((row) => ({
    ...row,
    plan: topicReviews(row.first, exam, intervals, finalReview),
  }));
  return { topics, exam, message: null, error: false };
}

export default function SpacedRepetitionSchedulerWidget() {
  const strings = useToolUi(SPACED_REPETITION_SCHEDULER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [rows, setRows] = useState<TopicRow[]>([
    { id: "t1", name: "", first: "" },
  ]);
  const [exam, setExam] = useState("");
  const [intervalText, setIntervalText] = useState(DEFAULT_INTERVAL_TEXT);
  const [finalReview, setFinalReview] = useState(true);
  const nextId = useRef(2);

  const today = useLocalToday((value) => {
    setRows([{ id: "t1", name: "", first: isoFromDay(value) }]);
    setExam(isoFromDay(value + DEFAULT_EXAM_DAYS));
  });

  const intervals = useMemo(() => parseIntervals(intervalText), [intervalText]);
  const built = useMemo(
    () => build(rows, exam, intervals, finalReview, strings),
    [rows, exam, intervals, finalReview, strings]
  );
  const calendar = useMemo(
    () => mergeReviewDays(built.topics.map((topic) => topic.plan)),
    [built.topics]
  );
  const formatDay = useMemo(
    () =>
      dayFormatter(locale, {
        weekday: "short",
        month: "short",
        day: "numeric",
      }),
    [locale]
  );
  const totalReviews = built.topics.reduce(
    (sum, topic) => sum + topic.plan.days.length,
    0
  );

  function updateRow(id: string, patch: Partial<TopicRow>) {
    setRows((current) =>
      current.map((row) => (row.id === id ? { ...row, ...patch } : row))
    );
  }

  function addRow() {
    const id = `t${nextId.current}`;
    nextId.current += 1;
    const first = today === null ? "" : isoFromDay(today);
    setRows((current) => [...current, { id, name: "", first }]);
  }

  function loadExample() {
    if (today === null) {
      return;
    }
    setRows(
      strings.exampleTopics.map((name, index) => ({
        id: `e${index}`,
        name,
        first: isoFromDay(today + (EXAMPLE_STARTS[index] ?? 0)),
      }))
    );
    setExam(isoFromDay(today + 21));
  }

  function reviewList(day: ReviewDay): string {
    return day.reviews
      .map((review) =>
        t(strings.reviewItem, {
          topic: built.topics[review.topic].name,
          number: fmt(review.number),
          total: fmt(review.of),
        })
      )
      .join(", ");
  }

  function handleIcs() {
    const uid = icsUidFactory(randomToken());
    const events: IcsAllDayEvent[] = calendar.map((day, index) => ({
      uid: uid(index),
      start: day.day,
      summary: t(strings.icsSummary, {
        topics: [
          ...new Set(
            day.reviews.map((review) => built.topics[review.topic].name)
          ),
        ].join(", "),
      }),
      description: t(strings.icsDescription, { items: reviewList(day) }),
    }));
    if (built.exam !== null) {
      events.push({
        uid: uid(events.length),
        start: built.exam,
        summary: strings.icsExam,
      });
    }
    downloadText(
      strings.icsFileName,
      buildIcsCalendar({
        prodId: "-//CitationEasy//Spaced Repetition Scheduler//EN",
        name: strings.icsCalendarName,
        stamp: new Date(),
        events,
      }),
      ICS_MIME
    );
  }

  return (
    <>
      <section className="space-y-4">
        <h2 className={WIDGET_H3}>{strings.topicsHeading}</h2>
        <ul className="space-y-3">
          {rows.map((row) => (
            <li
              className={`${CARD} grid gap-3 sm:grid-cols-[1fr_12rem_auto] sm:items-end`}
              key={row.id}
            >
              <TextField
                id={`sr-topic-${row.id}`}
                label={strings.topicLabel}
                onChange={(name) => updateRow(row.id, { name })}
                placeholder={strings.topicPlaceholder}
                value={row.name}
              />
              <TextField
                id={`sr-first-${row.id}`}
                label={strings.firstStudyLabel}
                onChange={(first) => updateRow(row.id, { first })}
                type="date"
                value={row.first}
              />
              <button
                className={BUTTON_SMALL}
                disabled={rows.length === 1}
                onClick={() =>
                  setRows((current) =>
                    current.filter((item) => item.id !== row.id)
                  )
                }
                type="button"
              >
                {strings.removeTopic}
              </button>
            </li>
          ))}
        </ul>
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_SECONDARY}
            disabled={rows.length >= 20}
            onClick={addRow}
            type="button"
          >
            {strings.addTopic}
          </button>
          <button
            className={BUTTON_SECONDARY}
            onClick={loadExample}
            type="button"
          >
            {strings.loadExample}
          </button>
        </div>
        <div className="grid gap-4 sm:grid-cols-[12rem_1fr]">
          <TextField
            id="sr-exam"
            label={strings.examLabel}
            onChange={setExam}
            type="date"
            value={exam}
          />
          <TextField
            hint={strings.intervalsHint}
            id="sr-intervals"
            label={strings.intervalsLabel}
            mono
            onChange={setIntervalText}
            value={intervalText}
          />
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <Checkbox
            checked={finalReview}
            label={strings.finalReview}
            onChange={setFinalReview}
          />
          <button
            className={BUTTON_SMALL}
            disabled={intervalText === DEFAULT_INTERVAL_TEXT}
            onClick={() => setIntervalText(DEFAULT_INTERVAL_TEXT)}
            type="button"
          >
            {strings.resetIntervals}
          </button>
        </div>
      </section>

      <section aria-live="polite" className="space-y-5">
        {built.message && built.error ? (
          <ErrorNotice>{built.message}</ErrorNotice>
        ) : null}
        {built.message && !built.error ? (
          <Notice>{built.message}</Notice>
        ) : null}
        {built.message === null && built.exam !== null ? (
          <>
            <h2 className={WIDGET_H2}>{strings.results}</h2>
            <StatGrid>
              <StatCard
                emphasis
                label={strings.reviewsStat}
                value={fmt(totalReviews)}
              />
              <StatCard
                label={strings.topicsStat}
                value={fmt(built.topics.length)}
              />
              <StatCard label={strings.daysStat} value={fmt(calendar.length)} />
            </StatGrid>
            <h3 className={WIDGET_H3}>{strings.perTopicHeading}</h3>
            <ul className="space-y-3">
              {built.topics.map((topic) => (
                <li
                  className={`${CARD} space-y-2`}
                  key={`${topic.name}-${topic.first}`}
                >
                  <p className="font-medium text-foreground">{topic.name}</p>
                  {topic.plan.days.length === 0 ? (
                    <p className="text-muted-foreground text-sm">
                      {t(strings.noReviews, { topic: topic.name })}
                    </p>
                  ) : (
                    <p className="text-foreground text-sm leading-relaxed">
                      {topic.plan.days
                        .map((day) =>
                          t(strings.reviewDate, {
                            date: formatDay(day),
                            offset: fmt(day - topic.first),
                          })
                        )
                        .join(" · ")}
                    </p>
                  )}
                  {topic.plan.compressed ? (
                    <p className={HINT}>{strings.compressed}</p>
                  ) : null}
                  {topic.plan.finalAdded ? (
                    <p className={HINT}>{strings.finalAdded}</p>
                  ) : null}
                </li>
              ))}
            </ul>
            {calendar.length > 0 ? (
              <>
                <h3 className={WIDGET_H3}>{strings.calendarHeading}</h3>
                <div className="max-h-[28rem] overflow-auto rounded-lg border border-border bg-card">
                  <table className={TABLE}>
                    <thead className="sticky top-0 bg-card">
                      <tr>
                        <th className={`${TH} whitespace-nowrap`} scope="col">
                          {strings.colDate}
                        </th>
                        <th className={`${TH} whitespace-nowrap`} scope="col">
                          {strings.colReviews}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {calendar.map((day) => (
                        <tr key={day.day}>
                          <td className={`${TD} whitespace-nowrap align-top`}>
                            {formatDay(day.day)}
                          </td>
                          <td className={TD}>{reviewList(day)}</td>
                        </tr>
                      ))}
                      <tr>
                        <td className={`${TD} whitespace-nowrap font-semibold`}>
                          {formatDay(built.exam)}
                        </td>
                        <td className={`${TD} font-semibold`}>
                          {strings.examRow}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <button
                  className={BUTTON_PRIMARY}
                  onClick={handleIcs}
                  type="button"
                >
                  {strings.downloadIcs}
                </button>
              </>
            ) : null}
          </>
        ) : null}
      </section>
    </>
  );
}
